import React, { useState, useEffect, useRef  } from "react";
import { AutoComplete, ConfigProvider } from "antd";
import { useDebounce } from "../../hooks/useDebounce";
import { useSelector, shallowEqual } from "react-redux";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";

import "./index.scss";

const creationPaths = {
  employee: "/hrpayroll/employees",
  customer: "/customers/addNewCustomer",
  supplier: "/purchase",
  group: "/inventory/item-group-entry/addNewGroup",
  item: "/inventory/product/addNewItem",
  location: "/settings/location-manage/addNewLocation",
  tag: "/settings/tags",
  category: "/inventory/category/addNewCategory",
  origin: "/inventory/origins-manage/addNewOrigin",
  // ... add more later
};

const SearchListAutoComplete = ({
  entityType,
  searchThunk,       // remote search (customers, items)
  resultSelector,    // remote: selector from redux
  optionsList = [],  // local: static list from redux
  onSelect,
  onEnter,
  value,
  placeholder,
  showAddNew = false,
  allowFreeTyping = false,
  onClear,
  allowClear = true,
  style,
  className, 
  disabled = false, 
}) => {
  const [searchText, setSearchText] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  
  const debouncedSearchText = useDebounce(searchText, 500);
  const reduxResults = useSelector(resultSelector || (() => []), shallowEqual);
  const searchResults = reduxResults || [];

  const navigate = useNavigate();
  const location = useLocation();

  const skipInternalClearRef = useRef(false);

  const isMobile = window.innerWidth < 768;

  // --- Remote search (API backed)
  useEffect(() => {
  if (!searchThunk) return;              // no thunk → no call
  if (debouncedSearchText.length < 1) return;

    // ✅ Call only once per debounce cycle
    searchThunk(debouncedSearchText);
  }, [debouncedSearchText, searchThunk]);

  

  useEffect(() => {
    if (!value) {
      setSearchText("");   // clear input if parent value is null
      setSelectedValue(null);
    } else if (value?.display_text !== undefined) {
      setSearchText(value?.display_text); // keep input synced if value exists
      setSelectedValue({
        value: value?.display_text,
        label: value?.display_text,
      });
      skipInternalClearRef.current = false; // ✅ reset after programmatic select
    } else if (value.label !== undefined) {
      setSearchText(value.label);
      setSelectedValue(value);
    }

  }, [value]);


  // --- Local search (frontend filter)
  useEffect(() => {
    if (!searchThunk && optionsList?.length > 0) {
      if (!searchText) {
        // show full list when input is empty
        setFilteredOptions(optionsList);
      } else {
        const filtered = optionsList?.filter((item) =>
          String(item?.display_text ?? "")
            .toLowerCase()
            .includes(String(searchText ?? "").toLowerCase())
        );

        setFilteredOptions(filtered);
      }
    }
  }, [searchText, optionsList, searchThunk]);

  // --- Decide final options
  const options = searchThunk
    ? searchResults?.map((item) => ({
      value: item?.display_text,
      label: item?.display_text,
      key: item?.sale_id, // keep ID in a hidden field
      raw: item,
    }))
    : filteredOptions?.map((item) => ({
      value: item?.display_text,
      label: item?.display_text,
      key: item?.sale_id,
      raw: item,
    }));

  const handleSelect = (value, option) => {
    if (option.value === "add_new") {
       skipInternalClearRef.current = true;
       onSelect?.("add_new");
    //   const path = creationPaths[entityType];
    //   if (path) {
    //     const returnTo = location.pathname;
    //     navigate(`${path}?returnTo=${returnTo}`);
    //   }
      return;
    }
    const selectedObject = option.raw || (searchThunk ? searchResults : optionsList).find(
      (opt) => opt?.sale_id?.toString() === option.key?.toString()
    );
    if (selectedObject) {
      setSelectedValue({
        value: selectedObject?.display_text,
        label: selectedObject?.display_text,
      });
      
      onSelect?.(selectedObject);
    } else {
      console.warn("⚠️ No matching object found for option", option);
      onSelect(null);
    }
  };

  const handleClear = () => {
    // newly added
    skipInternalClearRef.current = true;


    setSearchText("");
    setSelectedValue(null);

    // ✅ FORCE reset options (newly added)
    if (!searchThunk) {
    setFilteredOptions(optionsList);
    }
             
    onClear?.(); // Notify parent that field was cleared
    onSelect?.(null); // Also call onSelect with null for consistency
  };

  // NEW: Handle change for free typing
  const handleChange = (value) => {
    if (value === null || value === undefined) {
      // Field was cleared
      handleClear();
      return;
    }

    if (typeof value === "string") {
      setSearchText(value);
      if (allowFreeTyping) {
        onSelect?.({
          display_text: value,
          item_name: value,
          value: value,
          raw: null
        });
      }
    } else {
      // This handles the labelInValue format
      setSearchText(value?.label || "");
    }
  };

  return (
    <ConfigProvider
    >
      <div className={className || "searchListAutoComplete"} style={{ width: "100%", minWidth: entityType !== 'voucher' && 200, }}>
        <AutoComplete
          key={placeholder}
          labelInValue
          // Full name on hover, in case the table column is too narrow to
          // show it all -- the input itself truncates with an ellipsis (see
          // index.scss and the getInputElement title below) rather than
          // silently clipping without any indicator.
          title={searchText}
          // The dropdown panel defaults to matching the (often narrow) table
          // column width, which cuts off long item names with antd's
          // built-in ellipsis on the option text. Letting it size to its
          // content keeps full names readable regardless of column width.
          popupMatchSelectWidth={false}

          onOpenChange={(open) => {  // newly added
            if (open && !searchText && !searchThunk) {
              setFilteredOptions(optionsList);
            }
          }}

          options={[
            ...(showAddNew
              ? [
                {
                  value: "add_new",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 500,
                      }}
                    >
                      <PlusOutlined style={{ marginRight: 8 }} /> Add New
                    </div>
                  ),
                },
              ]
              : []),
            ...options,
          ]}
          // variant="filled"
          size="large"
          style={{ width: "100%", ...style }}
          popupstyle={{
            maxHeight: isMobile ? 320 : 480,
            overflowY: "auto",
          }}
          onClick={() => {
            if (selectedValue) {
              handleClear();
            }
          }}
          value={selectedValue || undefined}
          onSelect={(value, option) => handleSelect(value, option)}  // option = { value, label }
          onSearch={(text) => {
            if (skipInternalClearRef.current) {
              setSearchText(text);
              return;
            }
            setSearchText(text)

            // if (selectedValue) {
            //   setSelectedValue(null);
            // }

            // If user backspaces everything
              if (text === "") {
                setSelectedValue(null);
              }

            if (allowFreeTyping) {
              // ✅ also notify parent to update row text for typing UI
              onSelect?.({
                display_text: text,
                item_name: text,
                value: text,
                raw: null
              });
            }
          }}          
          disabled= {disabled}
          
          allowClear={allowClear} // Enable clear functionality
          onClear={handleClear}   // Handle clear button click
          // onChange={(val) => {
          //   if (typeof val === "string") {
          //     onSelect({ display_text: val, value: val });
          //   } else if (val?.value) {
          //     onSelect({ display_text: val.value, value: val.value });
          //   }
          // }}
         onChange={(val) => {
          // Clear case
        if (val === null || val === undefined || val === "") {
          handleClear();
          return;
        } 
        // If selection just happened, do NOTHING
        if (skipInternalClearRef.current) {
          skipInternalClearRef.current = false;
          return;
        }

        // Typing case (string)
        if (typeof val === "string") {
          setSearchText(val);
          return;
        }

        // labelInValue object (do not clear selectedValue)
        if (val?.label) {
          setSearchText(val.label);
        }
          // // If the user is typing/backspacing, clear the 'locked' selection 
          // // so the input can be free-edited.
          // setSelectedValue(null); 
          // setSearchText(val);
        
      }}


          onInputKeyDown={(e) => {
            if (e.key === "Enter" && onEnter) {
              onEnter(searchText);  // send typed text
            }
          }}

          placeholder={`Select ${placeholder}`}
          getInputElement={() => (
            <input style={{ padding: "6px 11px" }} title={searchText} />
          )}
        />
      </div>
    </ConfigProvider>
  );
};

export default SearchListAutoComplete;



